// src/pages/admin/ContactMessages.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/apiClient';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';

const normalizeMessages = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.data?.messages)) return response.data.messages;
  return [];
};

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [actionId, setActionId] = useState(null);

  const fetchMessages = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/contact');
      const list = normalizeMessages(response);
      // Sécurité : tri du plus récent au plus ancien
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setMessages(list);
    } catch (err) {
      setError('Erreur lors du chargement des messages. Veuillez réessayer plus tard.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const toggleRead = async (message) => {
    setActionId(message._id);
    try {
      await api.patch(`/contact/${message._id}`, { read: !message.read });
      showSuccessToast(message.read ? 'Message marqué comme non lu' : 'Message marqué comme lu');
      await fetchMessages();
    } catch (err) {
      showErrorToast('Impossible de mettre à jour le message. Veuillez réessayer.');
    } finally {
      setActionId(null);
    }
  };

  const deleteMessage = async (message) => {
    const confirmed = window.confirm(
      `Supprimer définitivement le message de ${message.name} (${message.email}) ?`
    );
    if (!confirmed) return;
    setActionId(message._id);
    try {
      await api.delete(`/contact/${message._id}`);
      showSuccessToast('Message supprimé avec succès');
      setExpandedId((prev) => (prev === message._id ? null : prev));
      await fetchMessages();
    } catch (err) {
      showErrorToast('Impossible de supprimer le message. Veuillez réessayer.');
    } finally {
      setActionId(null);
    }
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
        <p className="font-medium">Erreur</p>
        <p>{error}</p>
        <button
          onClick={fetchMessages}
          className="mt-2 bg-red-100 px-3 py-1 rounded-lg hover:bg-red-200"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Messages de contact</h1>
          {unreadCount > 0 && (
            <span className="bg-blue-600 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
              {unreadCount} non lu{unreadCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          onClick={fetchMessages}
          className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
          title="Actualiser les messages"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {messages.length === 0 ? (
        <div className="bg-white p-12 rounded-xl shadow-sm border border-slate-200 text-center text-gray-500">
          <p className="text-lg font-medium text-gray-700">Aucun message pour le moment</p>
          <p className="text-sm mt-1">Les messages envoyés via le formulaire de contact apparaîtront ici.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-br from-slate-50 to-slate-100">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {messages.map((message) => {
                  const id = message._id;
                  const isExpanded = expandedId === id;
                  const isBusy = actionId === id;
                  return (
                    <React.Fragment key={id}>
                      <tr
                        onClick={() => setExpandedId(isExpanded ? null : id)}
                        className={`cursor-pointer transition-colors duration-150 ${isExpanded ? 'bg-blue-50' : message.read ? 'hover:bg-slate-50' : 'bg-blue-50/40 hover:bg-blue-50 font-medium'}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          {message.read ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-700">
                              Lu
                            </span>
                          ) : (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              Non lu
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{message.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{message.company || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {message.createdAt ? new Date(message.createdAt).toLocaleString('fr-FR') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => toggleRead(message)}
                              disabled={isBusy}
                              className="px-3 py-1 text-xs font-medium rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                            >
                              {message.read ? 'Marquer non lu' : 'Marquer lu'}
                            </button>
                            <button
                              onClick={() => deleteMessage(message)}
                              disabled={isBusy}
                              className="px-3 py-1 text-xs font-medium rounded-lg bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-blue-50/60">
                          <td colSpan={6} className="px-6 py-4">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Message</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{message.message}</p>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactMessages;
